
import { useState } from 'react';
import AnimatedSection from '../base/AnimatedSection';

interface Question {
  id: number;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
  humor: string;
}

const questions: Question[] = [
  {
    id: 1,
    question: "Your website's headline should be:",
    options: [
      "About your company's 47-year history",
      "What your customer wants most",
      "Your founder's inspiring journey",
      "Industry buzzwords and synergy"
    ],
    correctAnswer: 1,
    explanation: "Your customer doesn't care about your story until they know you care about theirs.",
    humor: "Fun fact: Nobody wakes up thinking 'I wonder when that company was founded?'"
  },
  {
    id: 2,
    question: "The best call-to-action button says:",
    options: [
      "Click Here",
      "Submit",
      "Get My Free Strategy Call",
      "Learn More"
    ],
    correctAnswer: 2,
    explanation: "Specific, benefit-driven CTAs convert 3x better than generic ones.",
    humor: "Saying 'Click Here' is like saying 'Do the thing.' Very helpful. Much wow."
  },
  {
    id: 3,
    question: "When writing about benefits vs features:",
    options: [
      "Features are more important",
      "Benefits matter more",
      "They're equally important",
      "Neither matters much"
    ],
    correctAnswer: 1,
    explanation: "People buy benefits, not features. They want the hole, not the drill.",
    humor: "Features are what it does. Benefits are what it does FOR YOU. Like the difference between 'has wheels' and 'gets you places.'"
  },
  {
    id: 4,
    question: "Your About page should focus on:",
    options: [
      "Your credentials and awards",
      "Your customer's problems",
      "Your company timeline",
      "Your team's hobbies"
    ],
    correctAnswer: 1,
    explanation: "Even your About page should be about them, not you.",
    humor: "Plot twist: Your About page isn't actually about you. Mind = blown."
  },
  {
    id: 5,
    question: "The biggest copy mistake is:",
    options: [
      "Too many typos",
      "Being too salesy",
      "Talking about yourself",
      "Using big words"
    ],
    correctAnswer: 2,
    explanation: "When you make it about you instead of them, you lose them.",
    humor: "Talking about yourself in copy is like being that person at parties who only talks about their cat. We get it, Whiskers is amazing."
  }
];

const CopyQuiz = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [showExplanation, setShowExplanation] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [email, setEmail] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    setSelectedAnswer(answerIndex);
    setShowResult(true);
    
    const newAnswers = [...answers, answerIndex];
    setAnswers(newAnswers);
    
    if (answerIndex === questions[currentQuestion].correctAnswer) {
      setScore(score + 1);
    }
    
    setTimeout(() => {
      setShowExplanation(true);
    }, 500);
  };

  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowResult(false);
      setShowExplanation(false);
    } else {
      setQuizCompleted(true);
      setShowEmailForm(true);
    }
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setScore(0);
    setAnswers([]);
    setShowExplanation(false);
    setQuizCompleted(false);
    setShowEmailForm(false);
    setEmail('');
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const formData = new FormData();
    formData.append('email', email);
    formData.append('score', `${score}/${questions.length}`);
    formData.append('quiz_type', 'Copy Knowledge Quiz');
    
    try {
      await fetch('https://readdy.ai/api/form/d46fa2jhnd8fvojd242g', {
        method: 'POST',
        body: formData
      });
      
      setShowEmailForm(false);
      alert('Thanks! Your personalized copy audit is on its way.');
    } catch (error) {
      console.error('Error submitting quiz results:', error);
    }
  };

  const getScoreMessage = () => {
    const percentage = (score / questions.length) * 100;
    if (percentage >= 80) return "🎯 Copy Genius! You know your stuff.";
    if (percentage >= 60) return "📝 Pretty Good! A few tweaks and you're golden.";
    if (percentage >= 40) return "🤔 Getting There! Your copy has potential.";
    return "😅 Ouch! But hey, that's why we're here.";
  };

  if (quizCompleted && !showEmailForm) {
    return (
      <AnimatedSection className="bg-gradient-to-br from-[#a4ae9e]/10 to-[#708474]/10 rounded-2xl p-8 text-center">
        <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6">
          <i className="ri-trophy-line text-4xl text-[#708474]"></i>
        </div>
        <h3 className="text-2xl font-bold text-[#34444b] mb-4">Quiz Complete!</h3>
        <p className="text-xl text-[#73807c] mb-6">{getScoreMessage()}</p>
        <p className="text-lg text-[#34444b] mb-8">
          You scored <span className="font-bold text-[#708474]">{score}/{questions.length}</span>
        </p>
        <button
          onClick={resetQuiz}
          className="bg-[#708474] text-white px-8 py-3 rounded-lg hover:bg-[#73807c] transition-colors cursor-pointer whitespace-nowrap"
        >
          Take Quiz Again
        </button>
      </AnimatedSection>
    );
  }

  if (showEmailForm) {
    return (
      <AnimatedSection className="bg-gradient-to-br from-[#a4ae9e]/10 to-[#708474]/10 rounded-2xl p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <i className="ri-mail-line text-4xl text-[#708474]"></i>
          </div>
          <h3 className="text-2xl font-bold text-[#34444b] mb-4">Get Your Personalized Copy Audit</h3>
          <p className="text-lg text-[#73807c] mb-2">
            You scored <span className="font-bold text-[#708474]">{score}/{questions.length}</span>
          </p>
          <p className="text-[#34444b]">
            Want a free audit of your actual copy? Drop your email and we'll send you specific tips.
          </p>
        </div>
        
        <form onSubmit={handleEmailSubmit} className="max-w-md mx-auto" data-readdy-form>
          <div className="flex gap-4">
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="flex-1 px-4 py-3 border border-[#a4ae9e] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#708474] text-sm"
            />
            <button
              type="submit"
              className="bg-[#708474] text-white px-6 py-3 rounded-lg hover:bg-[#73807c] transition-colors cursor-pointer whitespace-nowrap"
            >
              Get My Audit
            </button>
          </div>
        </form>
      </AnimatedSection>
    );
  }

  const question = questions[currentQuestion];

  return (
    <AnimatedSection className="bg-gradient-to-br from-[#a4ae9e]/10 to-[#708474]/10 rounded-2xl p-8">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-medium text-[#73807c]">
            Question {currentQuestion + 1} of {questions.length}
          </span>
          <div className="flex space-x-1">
            {questions.map((_, index) => (
              <div
                key={index}
                className={`w-3 h-3 rounded-full ${
                  index <= currentQuestion ? 'bg-[#708474]' : 'bg-[#a4ae9e]/30'
                }`}
              />
            ))}
          </div>
        </div>
        
        <h3 className="text-xl font-bold text-[#34444b] mb-6">{question.question}</h3>
        
        <div className="space-y-3">
          {question.options.map((option, index) => (
            <button
              key={index}
              onClick={() => !showResult && handleAnswerSelect(index)}
              disabled={showResult}
              className={`w-full text-left p-4 rounded-lg border-2 transition-all cursor-pointer ${
                showResult
                  ? index === question.correctAnswer
                    ? 'border-green-500 bg-green-50 text-green-800'
                    : index === selectedAnswer
                    ? 'border-red-500 bg-red-50 text-red-800'
                    : 'border-[#a4ae9e]/30 bg-gray-50 text-gray-500'
                  : 'border-[#a4ae9e]/30 hover:border-[#708474] hover:bg-[#708474]/5'
              }`}
            >
              <div className="flex items-center">
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-[#a4ae9e]/20 text-sm font-medium mr-3">
                  {String.fromCharCode(65 + index)}
                </span>
                {option}
                {showResult && index === question.correctAnswer && (
                  <div className="w-5 h-5 flex items-center justify-center ml-auto">
                    <i className="ri-check-line text-green-600"></i>
                  </div>
                )}
                {showResult && index === selectedAnswer && index !== question.correctAnswer && (
                  <div className="w-5 h-5 flex items-center justify-center ml-auto">
                    <i className="ri-close-line text-red-600"></i>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
        
        {showExplanation && (
          <AnimatedSection animation="fadeIn" className="mt-6 p-4 bg-white rounded-lg border border-[#a4ae9e]/30">
            <p className="text-[#34444b] mb-2">
              <strong>Why:</strong> {question.explanation}
            </p>
            <p className="text-[#73807c] text-sm italic">{question.humor}</p>
          </AnimatedSection>
        )}
        
        {showExplanation && (
          <div className="mt-6 text-center">
            <button
              onClick={nextQuestion}
              className="bg-[#708474] text-white px-8 py-3 rounded-lg hover:bg-[#73807c] transition-colors cursor-pointer whitespace-nowrap"
            >
              {currentQuestion < questions.length - 1 ? 'Next Question' : 'See Results'}
            </button>
          </div>
        )}
      </div>
    </AnimatedSection>
  );
};

export default CopyQuiz;
